import type { JSX, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { ArrowLeft } from 'lucide-react'

export interface BreadcrumbItem {
  label: string
  to?: string
}

export interface PageHeaderProps {
  title: string
  description?: string
  breadcrumbs?: BreadcrumbItem[]
  actions?: ReactNode
  backTo?: string
}

/** Own component so the router hook runs only when a back button is requested. */
function BackButton({ to }: { to: string }): JSX.Element {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      aria-label="Back"
      onClick={() => navigate(to)}
      className="mb-2 inline-flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-gray-700"
    >
      <ArrowLeft className="h-4 w-4" />
      Back
    </button>
  )
}

/**
 * Standard admin page header: optional back button + breadcrumb + heading +
 * optional description + optional right-aligned actions slot.
 */
export default function PageHeader({ title, description, breadcrumbs, actions, backTo }: PageHeaderProps): JSX.Element {
  return (
    <div className="mb-6">
      {backTo !== undefined && <BackButton to={backTo} />}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2 flex items-center gap-1 text-sm text-gray-500">
          {breadcrumbs.map((crumb, idx) => (
            <span key={crumb.label} className="flex items-center">
              {idx > 0 && (
                <svg className="mx-1.5 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              )}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-gray-700 hover:underline transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-gray-900 font-medium">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h1>
          {description !== undefined && (
            <p className="mt-1 text-sm text-gray-400">{description}</p>
          )}
        </div>
        {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  )
}
